import type { ComponentProps, InputHTMLAttributes, ReactElement } from 'react'
import { useEffect, useMemo, useRef } from 'react'

import type { Theme } from '@emotion/react'
import type { UseFormReturn } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'

import { FormHelperText } from '@twisto/components/atoms/form-helper-text'
import { Typography } from '@twisto/components/atoms/typography'
import { validator } from '@twisto/form'

import { useCheckboxGroupControl } from './checkbox-group/checkbox-group.context'
import { CheckboxIcon } from './checkbox-icon'
import * as styles from './checkbox.styles'

export type Variant = 'default' | 'smaller'

type Props = {
  name: string
  helperText?: string
  label: ReactElement | ReactElement[]
  labelTypographyProps?: ComponentProps<typeof Typography>
  indeterminate?: boolean
  error?: boolean
  className?: string
  variant?: Variant
  disabled?: boolean
  required?: boolean
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'pattern' | 'required'>

export const Checkbox = ({
  label,
  labelTypographyProps,
  name,
  error: errorProp = false,
  id,
  indeterminate = false,
  disabled = false,
  helperText,
  className,
  variant = 'default',
  autoFocus = false,
  required = false,
  ...other
}: Props) => {
  const checkboxRef = useRef<HTMLInputElement | null>(null)
  const checkboxGroupCtx = useCheckboxGroupControl()
  // cast to ensure missing provider is handled
  const { register, watch, formState, clearErrors } =
    (useFormContext() as UseFormReturn | null) || {}

  const fieldErrors = useMemo(
    () => formState?.errors ?? {},
    [formState?.errors]
  )

  const isChecked = watch
    ? Boolean(watch(name))
    : checkboxRef.current?.checked ?? false

  const hasError = errorProp || Boolean(fieldErrors[name])

  useEffect(() => {
    if (isChecked) {
      clearErrors?.(name)
    }
  }, [clearErrors, fieldErrors, isChecked, name])

  useEffect(() => {
    if (checkboxGroupCtx) {
      checkboxGroupCtx.registerCheckbox(name)
    }
  }, [checkboxGroupCtx, name])

  useEffect(() => {
    if (checkboxRef.current && autoFocus) {
      checkboxRef.current.focus()
    }
  }, [autoFocus])

  const rules = {
    required: required ? validator.getValidationMessages().required : undefined,
  }

  const formProps = register?.(name, {
    ...rules,
    disabled,
    ...other,
  })

  const setRef = (ref: HTMLInputElement | null) => {
    checkboxRef.current = ref
    formProps?.ref(ref)
  }

  const labelTextStyles = [
    variant === 'smaller' ? styles.smallerLabelText : styles.defaultLabelText,
  ]

  return (
    <div className={className} css={styles.root}>
      <label css={styles.label} htmlFor={id ?? name}>
        <div>
          <input
            css={(theme: Theme) => [
              styles.input(theme),
              hasError && styles.inputError(theme),
            ]}
            data-testid="checkbox"
            disabled={disabled}
            id={id ?? name}
            name={name}
            type="checkbox"
            {...other}
            {...formProps}
            ref={setRef}
          />

          <CheckboxIcon
            css={styles.checkboxIcon}
            error={hasError}
            isChecked={isChecked}
            isDisabled={disabled}
            isIndeterminate={indeterminate}
          />
        </div>
        <Typography
          color="textSecondary"
          css={labelTextStyles}
          variant={variant === 'smaller' ? 'b4' : 'b2'}
          {...labelTypographyProps}
        >
          {required && '* '}
          {label}
        </Typography>
      </label>
      <FormHelperText
        css={styles.formHelperText}
        disabled={disabled}
        errors={fieldErrors}
        isError={hasError}
        name={name}
      >
        {helperText}
      </FormHelperText>
    </div>
  )
}
